import React, { useState, useEffect } from "react";
import { getTemplate } from "./TableStrategies/TableFactory";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DynamicTable = ({ sections, onSubmit }) => {
  const initializeState = (sections) => {
    const state = {};
    sections.forEach((section) => {
      section.rows.forEach((row) => {
        row.values.forEach((name) => {
          state[name] = ""; // Начальное значение для каждого инпута
        });
      });
    });
    return state;
  };

  const [inputValues, setInputValues] = useState(initializeState(sections));
  const [disabledInputs, setDisabledInputs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(0);

  // Функция для применения правил
  const applyRules = (rules) => {
    const newDisabledInputs = { ...disabledInputs };

    if (rules) {
      rules.forEach((rule) => {
        if (rule.type === "sum") {
          const sum = rule.sources.reduce(
            (acc, source) => acc + (parseFloat(inputValues[source]) || 0),
            0
          );
          setInputValues((prevState) => ({
            ...prevState,
            [rule.target]: sum.toString(),
          }));
        }

        if (rule.type === "gte") {
          const sum = rule.sources.reduce(
            (acc, source) => acc + (parseFloat(inputValues[source]) || 0),
            0
          );
          if (parseFloat(inputValues[rule.target]) < sum) {
            toast.error("Значение может быть только больше или равно");
            setInputValues((prevState) => ({
              ...prevState,
              [rule.target]: sum.toString(),
            }));
          }
        }

        if (rule.type === "disabled") {
          newDisabledInputs[rule.target] = true;
        }
      });

      setDisabledInputs(newDisabledInputs);
    }
  };

  // Применяем правила при изменении inputValues
  useEffect(() => {
    sections.forEach((section) => {
      if (section.rules) {
        applyRules(section.rules);
      }
    });
  }, [inputValues, sections]);

  const handleInputChange = (name, newValue) => {
    setInputValues((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  const handleSubmit = async () => {
    // Если форма уже была успешно отправлена и это не повторная отправка
    if (hasSubmitted) {
      toast.info("Отчет уже был отправлен. Дублирование отчетов не разрешено.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Фильтруем только незаблокированные значения
      const validInputs = Object.keys(inputValues).reduce((acc, key) => {
        if (!disabledInputs[key]) {
          acc[key] = inputValues[key];
        }
        return acc;
      }, {});

      if (onSubmit) {
        // Предполагаем, что onSubmit может быть асинхронным
        await Promise.resolve(onSubmit(validInputs));
        
        // Помечаем, что форма успешно отправлена
        setHasSubmitted(true);
        setSubmissionCount(prev => prev + 1);
        
        // Вывести уведомление
        toast.success("Отчет успешно отправлен! Повторная отправка не требуется.");
      }
    } catch (error) {
      toast.error("Ошибка при сохранении данных");
      console.error("Ошибка при сохранении:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Определение текста кнопки в зависимости от состояния
  const getButtonText = () => {
    if (isSubmitting) return "Сохранение...";
    if (hasSubmitted) return "Отправлено";
    return "Отправить";
  };

  return (
    <div>
      <ToastContainer />
      {sections.map((section, sectionIndex) => {
        const Template = getTemplate(section.type);
        return Template ? (
          <Template
            key={sectionIndex}
            {...section}
            inputValues={inputValues}
            disabledInputs={disabledInputs}
            onChange={handleInputChange}
          />
        ) : null;
      })}

      <button
        onClick={handleSubmit}
        disabled={isSubmitting || hasSubmitted}
        style={{
          marginTop: "20px",
          padding: "10px 20px",
          backgroundColor: hasSubmitted ? "#28a745" : isSubmitting ? "#6c757d" : "#007BFF",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: isSubmitting || hasSubmitted ? "not-allowed" : "pointer",
        }}
      >
        {getButtonText()}
      </button>
      
      {hasSubmitted && (
        <p style={{ 
          marginTop: "10px", 
          color: "#28a745", 
          fontSize: "14px" 
        }}>
          Отчет успешно отправлен. Повторная отправка не требуется.
        </p>
      )}
    </div>
  );
};

export default DynamicTable;